import { midiToFreq } from '../../../utils/math';
import { pickRandom } from '../../../utils/music/composerProgressions';
import { GrooveConsole } from './GrooveConsole';
import { CadenceType } from '../StructureEngine';
import { RhythmicDNAType, SongDNA } from '../ComposerSession';

export interface RhythmicCell {
    name: string;
    pattern: number[]; // Relative delays in seconds (base 120bpm approx)
}

/**
 * PatternGenerator
 * P3 FIX: Enhanced with section-locked DNA, cadence awareness, and articulation variation
 */
export class PatternGenerator {
    // FEATURE 3: Rhythmic DNA
    private activeDNA: RhythmicCell;
    private readonly RHYTHMIC_CELLS: RhythmicCell[] = [
        { name: 'Straight', pattern: [0, 0.25, 0.5, 0.75] }, // Classic Arp
        { name: 'Gallop', pattern: [0, 0.15, 0.5, 0.65] }, // Da-da... Da-da...
        { name: 'Tresillo', pattern: [0, 0.375, 0.75, 1.125] }, // 3-3-2 feel
        { name: 'Heartbeat', pattern: [0, 0.15] }, // Thump-thump
        { name: 'Sweep', pattern: [0, 0.05, 0.1, 0.15] }, // Fast roll
        { name: 'Waltz', pattern: [0, 0.33, 0.66] } // 3/4 feel
    ];

    // P3 FIX: Simple cadential pattern for phrase endings
    private readonly CADENTIAL_CELL: RhythmicCell = {
        name: 'Cadential', pattern: [0, 0.5] // Simple half-note feel
    };

    private grooveConsole: GrooveConsole;

    // AUDIT FIX: Song-level DNA (persistent throughout session)
    private songDNA: SongDNA | null = null;
    private songDNALocked: boolean = false;

    // Track current section for density variants
    private currentSection: 'Verse' | 'Chorus' | 'Bridge' = 'Verse';

    constructor(grooveConsole: GrooveConsole) {
        this.grooveConsole = grooveConsole;
        this.activeDNA = this.RHYTHMIC_CELLS[0];
        this.reset();
    }

    public reset() {
        // AUDIT FIX: Don't pick random DNA here anymore - wait for setSongDNA
        // Fallback to Straight if no DNA set yet
        if (!this.songDNALocked) {
            this.activeDNA = this.RHYTHMIC_CELLS[0];
        }
        this.currentSection = 'Verse';
    }

    /**
     * AUDIT FIX: Set the song-level DNA (called once at session start)
     * This establishes the rhythmic identity for the entire song
     */
    public setSongDNA(dna: SongDNA): void {
        this.songDNA = dna;
        this.songDNALocked = true;

        // Find the matching rhythmic cell by name
        const matchingCell = this.RHYTHMIC_CELLS.find(cell => cell.name === dna.primaryRhythm);
        this.activeDNA = matchingCell || this.RHYTHMIC_CELLS[0];

        if (process.env.NODE_ENV === 'development') {
            console.log(`[PatternGenerator] Song DNA locked: ${this.activeDNA.name} (${dna.grooveVariant}, ${dna.intensityBias})`);
        }
    }

    /**
     * AUDIT FIX: Called on section change to update density variant (NOT to change DNA)
     * The base DNA stays the same; only the probability of using it changes per section
     */
    public onSectionChange(newSection: 'Verse' | 'Chorus' | 'Bridge'): void {
        if (newSection !== this.currentSection) {
            this.currentSection = newSection;
            // AUDIT FIX: No longer randomize DNA here - use song DNA with section variants
            if (process.env.NODE_ENV === 'development' && this.songDNA) {
                console.log(`[PatternGenerator] Section ${newSection}: Using ${this.activeDNA.name} variant`);
            }
        }
    }

    /**
     * AUDIT FIX: Get section-specific density multiplier for DNA usage
     * Verse = 80% density (more space), Chorus = 100%+ (fuller), Bridge = 60% (sparser)
     */
    private getSectionDNADensity(): number {
        switch (this.currentSection) {
            case 'Verse':
                return 0.8;
            case 'Chorus':
                return 1.2;  // Fuller version of DNA
            case 'Bridge':
                return 0.6;  // Sparser version
            default:
                return 1.0;
        }
    }

    /**
     * PATTERN GENERATOR
     * Converts raw chord/bass/melody notes into scheduled Playable Notes.
     * FEATURE 3: Rhythmic DNA
     * Uses persistent rhythmic cells to create "hooks".
     * V10: Now includes density for Rhythmic Breathing.
     * P3 FIX: Added cadenceType and phraseFunction for cadence-aware patterns
     */
    public generatePattern(
        chordNotes: number[],
        bassNote: number,
        melodyNote: number,
        context: {
            section: 'Verse' | 'Chorus' | 'Bridge',
            tempoMult: number,
            baseVelocity: number,
            sectionDynamics: number,
            tuning: number,
            currentKeyOffset?: number,
            timbre?: any,
            density?: 'sparse' | 'normal' | 'dense',  // V10
            cadenceType?: CadenceType,  // P3 FIX
            phraseFunction?: 'Tension' | 'Resolution' | 'Neutral'  // P3 FIX
        }
    ): Array<{ freq: number, delay: number, velocity: number, duration?: number, timbre?: any }> {
        const notesToPlay: Array<{ freq: number, delay: number, velocity: number, duration?: number, timbre?: any }> = [];
        const {
            section, tempoMult, baseVelocity, sectionDynamics,
            tuning, currentKeyOffset = 0, timbre, density = 'normal',
            cadenceType = 'none', phraseFunction = 'Neutral'  // P3 FIX
        } = context;

        // Apply Key Offset Logic helper
        const getFreq = (midi: number) => midiToFreq(midi + currentKeyOffset, tuning);
        const tempoScale = 1 / tempoMult;

        // 1. CHORD GENERATION with RHYTHMIC DNA
        if (chordNotes.length > 0) {
            // P3 FIX: Use cadential pattern at phrase endings (simplify for resolution)
            const isApproachingCadence = cadenceType !== 'none';
            const isCadentialMoment = isApproachingCadence && (cadenceType === 'perfect' || cadenceType === 'half');

            // AUDIT FIX: Use persistent song DNA with section-specific density variants
            // Base probability modified by section DNA density
            const sectionDensityMult = this.getSectionDNADensity();
            let baseProbability = 0.5 * sectionDensityMult;

            // V10: Density modifiers (from ArrangementIntelligence)
            if (density === 'sparse') {
                baseProbability *= 0.5;  // Halve DNA chance in sparse phrases
            } else if (density === 'dense') {
                baseProbability *= 1.3;  // Boost DNA in dense phrases
            }

            // P3 FIX: Reduce complexity at cadence points
            if (isCadentialMoment) {
                baseProbability *= 0.3;  // Much lower chance of DNA at cadence
            }

            const roll = Math.random();
            const useDNA = roll < baseProbability && !isCadentialMoment;  // P3 FIX: No DNA at cadence

            if (useDNA) {
                // === PATH A: RHYTHMIC DNA (Arps / Textures) ===
                const dna = this.activeDNA || this.RHYTHMIC_CELLS[0];

                if (section === 'Chorus') {
                    // CHORUS: Use DNA strictly for ARPEGGIATION
                    let noteIndex = 0;
                    dna.pattern.forEach((offset, i) => {
                        const note = chordNotes[noteIndex % chordNotes.length];
                        noteIndex++;

                        const isStrong = i === 0 || offset === 0;
                        const humanVel = this.grooveConsole.humanizeVelocity(baseVelocity * sectionDynamics * 0.9, isStrong);
                        const humanDelay = this.grooveConsole.humanizeTiming(offset * tempoScale, 'chord');

                        notesToPlay.push({
                            freq: getFreq(note),
                            delay: humanDelay,
                            velocity: humanVel,
                            timbre
                        });
                    });
                } else {
                    // VERSE/BRIDGE: Broken Chord / Strum using DNA
                    chordNotes.forEach((note, index) => {
                        const dnaVal = dna.pattern[index % dna.pattern.length];
                        const humanVel = this.grooveConsole.humanizeVelocity(baseVelocity * sectionDynamics * 0.7, false);
                        const humanDelay = this.grooveConsole.humanizeTiming(dnaVal * 0.5 * tempoScale, 'chord');

                        notesToPlay.push({
                            freq: getFreq(note),
                            delay: humanDelay,
                            velocity: humanVel,
                            timbre
                        });
                    });
                }
            } else {
                // === PATH B: BLOCK CHORDS (Natural / Tight) ===
                // Play all notes roughly together, but with "human" imperfection.
                // P3 FIX: Add articulation variation based on phrase function
                const isLegato = phraseFunction === 'Resolution';  // Legato for resolution
                const isStaccato = phraseFunction === 'Tension';   // Staccato for tension

                chordNotes.forEach((note) => {
                    const humanVel = this.grooveConsole.humanizeVelocity(baseVelocity * sectionDynamics * 0.85, false);
                    // Tight Strum: 0-20ms spread (tighter for staccato)
                    const strumOffset = Math.random() * (isStaccato ? 0.01 : 0.02);
                    const humanDelay = this.grooveConsole.humanizeTiming(strumOffset, 'bass');

                    // P3 FIX: Calculate duration based on articulation
                    const baseDuration = 0.5;  // 500ms base
                    const duration = isStaccato ? baseDuration * 0.5 : (isLegato ? baseDuration * 1.5 : baseDuration);

                    notesToPlay.push({
                        freq: getFreq(note),
                        delay: humanDelay,
                        velocity: humanVel,
                        duration,  // P3 FIX: Add duration for articulation
                        timbre
                    });
                });
            }
            // BASS NOTE
            const bassVel = this.grooveConsole.humanizeVelocity(baseVelocity * sectionDynamics * 1.1, true);
            const bassDelay = this.grooveConsole.humanizeTiming(0, 'bass');

            notesToPlay.push({
                freq: getFreq(bassNote),
                delay: bassDelay,
                velocity: bassVel,
                timbre
            });
        }

        // 2. MELODY GENERATION
        const melodyVel = this.grooveConsole.humanizeVelocity(baseVelocity * sectionDynamics, true);
        const melodyDelay = this.grooveConsole.humanizeTiming(0, 'melody');
        notesToPlay.push({
            freq: getFreq(melodyNote),
            delay: melodyDelay,
            velocity: melodyVel,
            timbre
        });

        return notesToPlay;
    }
}
